/**
 * Log levels for categorizing log messages
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4
}

/**
 * Log categories for better organization
 */
export enum LogCategory {
  AUTH = 'Authentication',
  DATABASE = 'Database',
  STORAGE = 'Storage',
  UI = 'User Interface',
  NETWORK = 'Network',
  PERFORMANCE = 'Performance',
  SECURITY = 'Security',
  GENERAL = 'General'
}

/**
 * Log entry structure with enhanced metadata
 */
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  message: string;
  context?: Record<string, any>;
  userId?: string;
  sessionId?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs: number = 100;
  private minLevel: LogLevel = LogLevel.INFO;

  constructor() {
    // Load settings from localStorage if available
    this.loadSettings();
    // Load existing logs from localStorage
    this.loadLogs();
  }

  private loadSettings() {
    try {
      const savedMinLevel = localStorage.getItem('logger_minLevel');
      if (savedMinLevel) {
        this.minLevel = parseInt(savedMinLevel);
      }
      
      const savedMaxLogs = localStorage.getItem('logger_maxLogs');
      if (savedMaxLogs) {
        this.maxLogs = parseInt(savedMaxLogs);
      }
    } catch (e) {
      console.error('Error loading logger settings:', e);
    }
  }

  private saveSettings() {
    try {
      localStorage.setItem('logger_minLevel', this.minLevel.toString());
      localStorage.setItem('logger_maxLogs', this.maxLogs.toString());
    } catch (e) {
      console.error('Error saving logger settings:', e);
    }
  }

  private loadLogs() {
    try {
      const savedLogs = localStorage.getItem('application_logs');
      if (savedLogs) {
        this.logs = JSON.parse(savedLogs);
      }
    } catch (e) {
      console.error('Error loading logs:', e);
    }
  }

  setMinLevel(level: LogLevel) {
    this.minLevel = level;
    this.saveSettings();
  }

  setMaxLogs(max: number) {
    this.maxLogs = max;
    this.saveSettings();
  }

  private addLog(level: LogLevel, message: string, category: LogCategory = LogCategory.GENERAL, context?: Record<string, any>, userId?: string, sessionId?: string) {
    if (level < this.minLevel) return;
    
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      context,
      userId,
      sessionId
    };
    
    // Add to in-memory logs
    this.logs.push(entry);
    
    // Trim logs if exceeding max
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
    
    // Also log to console
    this.logToConsole(entry);
    
    // Store in localStorage
    this.persistLogs();
  }

  private logToConsole(entry: LogEntry) {
    const prefix = `[${entry.timestamp}] [${LogLevel[entry.level]}]`;
    
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.INFO:
        console.info(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.WARN:
        console.warn(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.ERROR:
        console.error(prefix, entry.message, entry.context || '');
        break;
    }
  }

  private persistLogs() {
    try {
      localStorage.setItem('application_logs', JSON.stringify(this.logs));
    } catch (e) {
      console.error('Error persisting logs:', e);
    }
  }

  getLogs() {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    localStorage.removeItem('application_logs');
  }

  /**
   * Log a debug message
   */
  debug(message: string, categoryOrContext?: LogCategory | Record<string, any>, context?: Record<string, any>, userId?: string, sessionId?: string) {
    const category = this.getCategoryFromParam(categoryOrContext);
    const ctx = this.getContextFromParam(categoryOrContext, context);
    this.addLog(LogLevel.DEBUG, message, category, ctx, userId, sessionId);
  }

  /**
   * Log an informational message
   */
  info(message: string, categoryOrContext?: LogCategory | Record<string, any>, context?: Record<string, any>, userId?: string, sessionId?: string) {
    const category = this.getCategoryFromParam(categoryOrContext);
    const ctx = this.getContextFromParam(categoryOrContext, context);
    this.addLog(LogLevel.INFO, message, category, ctx, userId, sessionId);
  }

  /**
   * Log a warning message
   */
  warn(message: string, categoryOrContext?: LogCategory | Record<string, any>, context?: Record<string, any>, userId?: string, sessionId?: string) {
    const category = this.getCategoryFromParam(categoryOrContext);
    const ctx = this.getContextFromParam(categoryOrContext, context);
    this.addLog(LogLevel.WARN, message, category, ctx, userId, sessionId);
  }

  /**
   * Log an error message
   */
  error(message: string, categoryOrContext?: LogCategory | Record<string, any>, context?: Record<string, any>, userId?: string, sessionId?: string) {
    const category = this.getCategoryFromParam(categoryOrContext);
    const ctx = this.getContextFromParam(categoryOrContext, context);
    this.addLog(LogLevel.ERROR, message, category, ctx, userId, sessionId);
  }

  /**
   * Log a critical error message
   */
  critical(message: string, categoryOrContext?: LogCategory | Record<string, any>, context?: Record<string, any>, userId?: string, sessionId?: string) {
    const category = this.getCategoryFromParam(categoryOrContext);
    const ctx = this.getContextFromParam(categoryOrContext, context);
    this.addLog(LogLevel.CRITICAL, message, category, ctx, userId, sessionId);
  }

  /**
   * Helper method to determine if a parameter is a LogCategory
   */
  private isLogCategory(param: any): param is LogCategory {
    return typeof param === 'string' && Object.values(LogCategory).includes(param as LogCategory);
  }

  /**
   * Helper method to extract category from parameter
   */
  private getCategoryFromParam(param?: LogCategory | Record<string, any>): LogCategory {
    if (this.isLogCategory(param)) {
      return param;
    }
    return LogCategory.GENERAL;
  }

  /**
   * Helper method to extract context from parameters
   */
  private getContextFromParam(categoryOrContext?: LogCategory | Record<string, any>, context?: Record<string, any>): Record<string, any> | undefined {
    if (!categoryOrContext) {
      return context;
    }
    
    if (this.isLogCategory(categoryOrContext)) {
      return context;
    }
    
    return categoryOrContext as Record<string, any>;
  }
}

// Export a singleton instance
export const logger = new Logger();